import React, { useState, useRef, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Search, Plus, X, Building2, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * VendorCombobox — searchable creditor picker for the Purchase Bill form.
 *
 *  • Type-ahead filtering, case-insensitive.
 *  • Inline "+ Add new creditor" button shown when typed string matches
 *    nothing — opens a tiny inline create form, POSTs to /api/clients/,
 *    then auto-selects the freshly-created vendor.
 *  • On selection, calls onSelect(vendor) so the parent can autofill
 *    GSTIN + Address fields from vendor.gstin / vendor.address.
 *
 *  Props:
 *    value       — current vendor_name (string)
 *    onChange    — (name) => void   (for plain typing)
 *    onSelect    — (vendor obj) => void   (when picked from dropdown OR newly created)
 *    placeholder — input placeholder
 *    testid      — root data-testid prefix
 */
export default function VendorCombobox({ value, onChange, onSelect, placeholder = 'Search vendor…', testid = 'vendor-combobox' }) {
  const [vendors, setVendors] = useState([]);
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [creatingForm, setCreatingForm] = useState({ name: '', gstin: '', phone: '', address: '' });
  const ref = useRef();

  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/clients/?type=Creditor`, { withCredentials: true });
        const raw = Array.isArray(r.data) ? r.data : [];
        setVendors(raw.filter(c => c.type === 'Creditor' || c.type === 'Vendor' || c.type === 'Both'));
      } catch (e) { console.error(e); }
    })();
  }, []);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return vendors.slice(0, 30);
    return vendors.filter(v => (v.name || '').toLowerCase().includes(q)).slice(0, 30);
  }, [vendors, query]);

  const exactMatch = useMemo(
    () => vendors.find(v => (v.name || '').toLowerCase() === (query || '').trim().toLowerCase()),
    [vendors, query],
  );

  const pick = (v) => {
    setQuery(v.name);
    onChange?.(v.name);
    onSelect?.(v);
    setOpen(false);
  };

  const startCreate = () => {
    setCreatingForm(f => ({ ...f, name: query.trim() }));
    setCreating(true);
  };

  const doCreate = async () => {
    const f = creatingForm;
    if (!f.name.trim()) return;
    try {
      const r = await axios.post(`${API}/clients/`, { ...f, type: 'Creditor' }, { withCredentials: true });
      const created = r.data;
      setVendors(prev => [created, ...prev]);
      pick(created);
      setCreating(false);
      setCreatingForm({ name: '', gstin: '', phone: '', address: '' });
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'Failed to create creditor');
    }
  };

  return (
    <div ref={ref} className="relative" data-testid={testid}>
      <div className="relative">
        <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-600" />
        <input
          data-testid={`${testid}-input`}
          value={query}
          onChange={e => { setQuery(e.target.value); onChange?.(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-7 pr-7 py-2 text-zinc-100 text-sm focus:outline-none focus:border-amber-500"
        />
        {exactMatch && (
          <Check size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-emerald-400" />
        )}
      </div>
      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl z-50 max-h-72 overflow-y-auto"
          data-testid={`${testid}-dropdown`}>
          {filtered.length === 0 && !creating && (
            <div className="p-3 text-center">
              <div className="text-zinc-500 text-xs mb-2">No vendor matches "{query}"</div>
              <button onClick={startCreate}
                data-testid={`${testid}-add-new`}
                className="inline-flex items-center gap-1.5 text-xs bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-200 px-3 py-1.5 rounded-lg transition-colors">
                <Plus size={11} /> Create "{query.trim()}" as new creditor
              </button>
            </div>
          )}
          {!creating && filtered.map(v => (
            <button key={v.id}
              type="button"
              onClick={() => pick(v)}
              data-testid={`${testid}-option-${v.id}`}
              className="w-full text-left px-3 py-2 hover:bg-zinc-800 border-b border-zinc-800/50 last:border-0">
              <div className="flex items-center gap-2">
                <Building2 size={12} className="text-amber-400 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-zinc-100 text-sm truncate">{v.name}</div>
                  <div className="text-zinc-500 text-[10px] truncate">
                    {v.gstin || '—'}
                    {v.phone && ` · ${v.phone}`}
                    {v.address && ` · ${v.address.slice(0, 30)}${v.address.length > 30 ? '…' : ''}`}
                  </div>
                </div>
              </div>
            </button>
          ))}
          {!creating && filtered.length > 0 && !exactMatch && query.trim().length >= 2 && (
            <button onClick={startCreate}
              type="button"
              data-testid={`${testid}-add-new-inline`}
              className="w-full text-left px-3 py-2 hover:bg-amber-500/10 border-t border-zinc-800 flex items-center gap-2 text-amber-300 text-xs">
              <Plus size={11} /> Create "{query.trim()}" as new creditor
            </button>
          )}
          {creating && (
            <div className="p-3 space-y-2 bg-zinc-950" data-testid={`${testid}-create-form`}>
              <div className="flex items-center justify-between">
                <div className="text-zinc-200 text-xs font-semibold">New Creditor</div>
                <button onClick={() => setCreating(false)} className="text-zinc-500 hover:text-zinc-300"><X size={12} /></button>
              </div>
              <input value={creatingForm.name}
                onChange={e => setCreatingForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Name *"
                className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-xs text-zinc-100 focus:outline-none focus:border-amber-500" />
              <input value={creatingForm.gstin}
                onChange={e => setCreatingForm(f => ({ ...f, gstin: e.target.value }))}
                placeholder="GSTIN (optional)"
                className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-xs text-zinc-100 focus:outline-none focus:border-amber-500" />
              <input value={creatingForm.phone}
                onChange={e => setCreatingForm(f => ({ ...f, phone: e.target.value }))}
                placeholder="Phone (optional)"
                className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-xs text-zinc-100 focus:outline-none focus:border-amber-500" />
              <textarea value={creatingForm.address}
                onChange={e => setCreatingForm(f => ({ ...f, address: e.target.value }))}
                placeholder="Address (optional)"
                rows={2}
                className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-xs text-zinc-100 focus:outline-none focus:border-amber-500" />
              <button onClick={doCreate}
                data-testid={`${testid}-create-submit`}
                className="w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs py-1.5 rounded">
                Save & Select
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
